import { Form8233Model } from "../../../Models/Form8233Models";
import { Form8233Service } from "../../../services/Form8233Service";
import { formatDateForXML, normalizeDateToSlash } from "../../../Utility/utility";
import { escapeXmlText, getPDFBase64 } from "./generateW8BENXML";
import { getStateCode } from "../../../utils/formUtils";

export async function generateForm8233XML(data: any): Promise<string> {
  let formModel: Form8233Model;
  let isMapDone = data?.isMapDone || false;

  if (!isMapDone) {
    formModel = Form8233Service.fromApiResponse(data);

    // Set isSigned based on signature value when mapping from API response
    const signatureField =
      formModel["Form-8233-Part_4_signature"] ||
      formModel["Form-8233-Part_3_signature"] ||
      formModel["Signature"];

    if (signatureField && typeof signatureField === "string") {
      const hasSignature = signatureField.trim() !== "";
      formModel["isSigned"] = hasSignature ? "yes" : "no";
    }
  } else {
    formModel = new Form8233Model(data);
  }

  // Generate original file XML if viewPdf is provided
  const generateOriginalFileXml = async () => {
    const viewPdfUrl = formModel["viewPdf"] || "";
    if (viewPdfUrl) {
      try {
        const originalFileContent = await getPDFBase64(viewPdfUrl, formModel["fileImportId"]);
        const fileName = viewPdfUrl.split("/").pop() || "original_document.pdf";

        return `
        <OriginalFile>
          <Name>${fileName}</Name>
          <Data>${originalFileContent}</Data>
        </OriginalFile>`;
      } catch (error) {
        console.error("Error converting original PDF to base64:", error);
        return "<OriginalFile></OriginalFile>";
      }
    }
    return "<OriginalFile></OriginalFile>";
  };

  const getValue = (fieldName: string): string => {
    const value = formModel[fieldName] || "";
    return String(value);
  };

  const formatBoolean = (fieldName: string): string => {
    const value = formModel[fieldName];
    if (
      value === true ||
      value === "yes" ||
      value === "Yes" ||
      value === "selected" ||
      value === "true"
    ) {
      return "true";
    }
    return "false";
  };

  const formatDate = (fieldName: string): string => {
    const value = formModel[fieldName];
    if (!value) return "";

    // First convert to ISO format using the utility
    const isoDate = formatDateForXML(value);
    if (!isoDate) return "";

    // Convert from YYYY-MM-DD to MM-DD-YYYY
    const dateMatch = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return `${month}-${day}-${year}`;
    }

    return isoDate; // fallback to original if parsing fails
  };

  const originalFileXml = await generateOriginalFileXml();
  console.log(
    "Original File XML for Form8233:",
    getValue("Form-8233-Part_1_foreign_student_trainee_researcher")
  );
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<FORM_8233_SEP_2018>
    <UniqueIdentifier>${getValue("UniqueIdentifier")}</UniqueIdentifier>
    <UIDreferenceline>False</UIDreferenceline>
    <Instructor_Identifier>${getValue("entityUniqueIdentifier")}</Instructor_Identifier>
    <BusinessUnit>${getValue("BusinessUnit")}</BusinessUnit>
    <Entity></Entity>
    <CommonInfo>
      <EntityTypes>
        <EntityType />
      </EntityTypes>
      <EntityClassification />
      <InitialEntityTypeSelection>Non U.S. Individual</InitialEntityTypeSelection>
      <FATCAEntityType />
      <Individual>
        <FirstName>${getValue("Form-8233-Part_1_individual_name").split(" ")[0]}</FirstName>
        <LastName>${getValue("Form-8233-Part_1_individual_name").split(" ").slice(1).join(" ")}</LastName>
        <CountryOfBirth>${getValue("8233_Country_issuing_passport")}</CountryOfBirth>
        <CountryOfBirthOther />
        <DateOfBirth></DateOfBirth>
        <CountryOfCitizenshipForTax>${getValue("8233_Country_issuing_passport")}</CountryOfCitizenshipForTax>
        <CountryOfCitizenshipForTaxOther />
        <CityTownOfBirth />
        <PlaceofBirth />
        <AdditionalTaxJurisdictions />
      </Individual>
      <Business>
        <Name />
        <Country />
        <CountryOther />
        <DoingBusinessAsName />
        <DREFATCAEntitytype />
        <JurisdictionTaxCountry />
        <SponsoringEntityName />
        <SponsoringEntityGIIN />
        <TrusteeName />
        <TrusteeGIIN />
        <AdditionalTaxJurisdictions />
        <IsSpecifiedUSPerson></IsSpecifiedUSPerson>
        <SpecifiedPersonUS>NA</SpecifiedPersonUS>
        <IsSpecifiedUKPerson>true</IsSpecifiedUKPerson>
        <SpecifiedPersonUK>NA</SpecifiedPersonUK>
        <IsSpecifiedCRSPerson>true</IsSpecifiedCRSPerson>
        <SpecifiedPersonCRS>NA</SpecifiedPersonCRS>
      </Business>
      <PermanentAddress>
        <Line1>${getValue("Form-8233-Part_1_permanent_address_street")}</Line1>
        <Line2>${getValue("Permanent address line 2")}</Line2>
        <Line3>${getValue("Permanent address line 3")}</Line3>
        <City_Town>${getValue("Permanent address city")}</City_Town>
        <State_Province>${getStateCode(getValue("Permanent address state NonUS") || getStateCode(getValue("Permanent address state USCA")))}</State_Province>
        <Country>${getValue("Form-8233-Part_1_permanent_address_country")}</Country>
        <CountryOther>${getValue("Permanent address other")}</CountryOther>
        <Zip_PostalCode>${getValue("Permanent address zip")}</Zip_PostalCode>
        <IsCare_of_address>${formatBoolean("Permanent address is care of")}</IsCare_of_address>
        <IsP_O_Box>${formatBoolean("Permanent address is po box")}</IsP_O_Box>
        <IsRuralRoute>${formatBoolean("Permanent address is rural route")}</IsRuralRoute>
      </PermanentAddress>
      <MailingAddress>
        <Line1 >${getValue("Form-8233-Part_1_us_address_street")}</Line1>
        <Line2 >${getValue("US address line 2")}</Line2>
        <Line3 >${getValue("US address line 3")}</Line3>
        <City_Town >${getValue("US address city")}</City_Town>
        <State_Province >${getStateCode(getValue("US address state USCA"))}</State_Province>
        <Country >${getValue("US address country")}</Country>
        <CountryOther >${getValue("US address other")}</CountryOther>
        <Zip_PostalCode >${getValue("US address zip")}</Zip_PostalCode>
        <IsCare_of_address>${formatBoolean("US address is care of")}</IsCare_of_address>
        <IsP_O_Box>${formatBoolean("US address is po box")}</IsP_O_Box>
        <IsRuralRoute>${formatBoolean("US address is rural route")}</IsRuralRoute>
      </MailingAddress>
      <ContactInfo>
        <FirstName>${getValue("FirstName")}</FirstName>
        <LastName>${getValue("LastName") || getValue("Last_name")}</LastName>
        <Email>${getValue("Email")}</Email>
        <SignatoryEmail />
        <TelephoneCountry>${getValue("TelephoneCountry")}</TelephoneCountry>
        <TelephoneNumber>${getValue("TelephoneNumber")}</TelephoneNumber>
        <CellNumber />
      </ContactInfo>
      <SubsidiaryInfo>
        <SubsidiaryId>2799</SubsidiaryId>
        <SubsidiaryName>Group Tax</SubsidiaryName>
        <SubsidiaryContactName />
        <SubsidiaryContactTelephone />
        <SubsidiaryContactEmail />
      </SubsidiaryInfo>
      <CitizenshipInfo>
        <IsUSPerson />
        <IsUSGreenCardHolder />
        <IsDualCitizenshipHolder />
        <DoesDualCitizenshipIncludeAmerica />
        <IsBornOrganisedInUS />
        <HasRenouncedCitizenship />
        <DateRenouncedCitizenship />
        <AreCountryForTaxAndBirthDifferent />
        <InUSA31DaysCurrentYear />
        <DaysInCurrentYear>${getValue("DaysInCurrentYear")}</DaysInCurrentYear>
        <DaysInPrecedingYear>${getValue("DaysInPrecedingYear")}</DaysInPrecedingYear>
        <DaysInFurtherPrecedingYear>${getValue("DaysInFurtherPrecedingYear")}</DaysInFurtherPrecedingYear>
      </CitizenshipInfo>
      <IsOnBehalfOfSomeoneElse>False</IsOnBehalfOfSomeoneElse>
      <Proxy>
        <FirstName></FirstName>
        <LastName></LastName>
        <Capacity />
        <CapacityOther />
        <PrimaryContactNumber />
      </Proxy>
      <NoUSSiDeclarationConfirmed />
      <NoUSSiDeclarationPart2_3Skipped>False</NoUSSiDeclarationPart2_3Skipped>
      <IgnoredRules />
      <NonUSIndividual_IsClaimingECI />
      <NonUSIndividual_IsForIndependentPersonalServices />
      <NonUSIndividual_IsPublicEntertainer />
      <NonUSIndividual_IsResidentNoSpecificProvision />
      <NonUSIndividual_IsForDependentPersonalServices />
      <NonUSIndividual_IsForScholarshipFellowshipAndPersonalServicesIncome />
      <NonUSIndividual_PartOfWhichNotClaimingTaxTreatyWithholdingExemption />
      <NonUSIndividual_IsNotClaimingTaxTreatyWithholdingExemption />
      <NonUSIndividual_IsForScholarshipFellowshipNoPersonalServicesIncome />
      <NonUSIndividual_IsForOnlyForeignStatusOrTreatyBenefits />
      <SubmissionGroupId>b3c42996-cba4-405f-8f12-8390107b4e15</SubmissionGroupId>
      <SelectedOptionsonGuide4 />
    </CommonInfo>
    <TaxpayerIdentification>
      <Type>${getValue("Taxpayer type") || ""}</Type>
      <GIINAppliedFor>False</GIINAppliedFor>
      <GIINNotApplicable />
      <SSNOrITIN>${getValue("SSNOrITIN") || getValue("Form-8233-Part_1_us_tax_id")}</SSNOrITIN>
      <ForeignTaxpayerId>${getValue("Form-8233-Part_1_ftin")}</ForeignTaxpayerId>
      <TINFormatNotAvail>False</TINFormatNotAvail>
      <ForeignTaxpayerCountry>${getValue("ForeignTaxpayerCountry")}</ForeignTaxpayerCountry>
      <ForeignTinUnavailable>False</ForeignTinUnavailable>
      <ForeignTinUnavailableReason />
      <IsAppliedForTIN>False</IsAppliedForTIN>
      <UnexpectedTINTypeExplanation />
      <UnexpectedTINTypeExplanationConfirmed />
      <UnexpectedTINTypeContentBlock />
      <CheckifFTINnotlegallyrequired>False</CheckifFTINnotlegallyrequired>
      <IsFurtherExplanation>False</IsFurtherExplanation>
      <WrittenStatementReasons />
      <WrittenStatementReasonsAdditionalInformation />
      <TINCheck>
        <IsCheckPreformed>False</IsCheckPreformed>
        <IsSuccessful>False</IsSuccessful>
        <Date></Date>
      </TINCheck>
    </TaxpayerIdentification>
    <SignInfo>
      <IsSigned>${formatBoolean("isSigned")}</IsSigned>
      <By>${getValue("Form-8233-Part_3_signature")}</By>
      <Capacity />
      <Date>${normalizeDateToSlash(getValue("Form-8233-Part_3_signature_date"))}</Date>
      <DateOnForm>${getValue("Signature_date_on_form") || getValue("Form-8233-Part_3_signature_date")}</DateOnForm>
      <SignatureUnreadable>${formatBoolean("Signature_unreadable")}</SignatureUnreadable>
      <UserLocalTime>08-27-2024 1:00:19 PM BST</UserLocalTime>
      <UserLocalTimeZone>British Summer Time</UserLocalTimeZone>
      <EnteredConfirmationCode>L8LQ6P</EnteredConfirmationCode>
    </SignInfo>
    <Tag>
      <SubmissionDate></SubmissionDate>
      <IsFromComplyExchangeService>True</IsFromComplyExchangeService>
      <IsFromBureau>False</IsFromBureau>
      <IsPaperForm>False</IsPaperForm>
      <IsE_form>True</IsE_form>
      <CreatedByApp>Extractor</CreatedByApp>
      <RetroactiveStatement>
        <IsSigned>False</IsSigned>
        <SignedBy></SignedBy>
        <SignDate></SignDate>
        <EffectiveDate></EffectiveDate>
        <ChangeinCircumstanceDate></ChangeinCircumstanceDate>
        <ReasonForChange />
        <Explanation />
        <RetroSignBy />
        <StatementText>I declare, under penalties of perjury, that I have examined the Form $$formtype$$ submission that I am providing and that the information and certifications contained therein remain the same and unchanged throughout the period beginning on $$date$$ to the date of the submission here and that they were true, correct and complete during the entire period.If any information or certifications were not true, correct and complete for that entire period enter the date from when they were, providing additional written information below supporting the changes in circumstances back to the account opening date provided above.</StatementText>
      </RetroactiveStatement>
    </Tag>
    <RelatedDocuments>
     
    </RelatedDocuments>
    ${originalFileXml}
    <CalendarYear>${getValue("Form-8233-Calendar_year")}</CalendarYear>
    <TaxYearStart>${getValue("Form-8233-Beginning_year")}</TaxYearStart>
    <TaxYearEnd>${getValue("Form-8233-Ending_year")}</TaxYearEnd>
    <USVisaType>${getValue("Form-8233-Part_1_us_visa_type")}</USVisaType>
    <CountryIssuingPassport>${getValue("Form-8233-Part_1_passport_country")}</CountryIssuingPassport>
    <CountryIssuingPassportOther>${getValue("Other")}</CountryIssuingPassportOther>
    <PassportNumber>${getValue("Form-8233-Part_1_passport_number")}</PassportNumber>
    <USEntryDate>${formatDate("Form-8233-Part_1_date_entry_to_us")}</USEntryDate>
    <NonimmigrantStatus>${getValue("Form-8233-Part_1_current_nonimmigrant_status")}</NonimmigrantStatus>
    <NonimmigrantStatusNA>${formatBoolean("Current nonimmigrant status Not Applicable")}</NonimmigrantStatusNA>
    <NonimmigrantStatusExpiryDate>${formatDate("Form-8233-Part_1_current_nonimmigrant_status_expires")}</NonimmigrantStatusExpiryDate>
    <PartILine10>${formatBoolean("Form-8233-Part_1_foreign_student_trainee_researcher")}</PartILine10>
    <PersonalServicesProvided>${getValue("Form-8233-Part_2_personal_services")}</PersonalServicesProvided>
    <PersonalServicesValue>${getValue("Form-8233-Part_2_total_compensation_expected")}</PersonalServicesValue>
    <PartIILine12aTaxTreatyCountry>${getValue("Form-8233-Part_2_tax_treaty")}</PartIILine12aTaxTreatyCountry>
    <PartIILine12aTaxTreatyArticle>${getValue("Form-8233-Part_2_treaty_article")}</PartIILine12aTaxTreatyArticle>
    <PartIILine12bExemptTotal>${getValue("Form-8233-Part_2_total_compensation_listed_tax_under_treaty")}</PartIILine12bExemptTotal>
    <PartIILine12bExemptTotalAll>${formatBoolean("12_All is exempt from tax")}</PartIILine12bExemptTotalAll>
    
    <PermanentResidenceCountry>${getValue("Form-8233-Part_2_country_residence")}</PermanentResidenceCountry>
    <PermanentResidenceCountryOther />
    <PartIILine13aAmount>${getValue("Form-8233-Part_2_scholarship_amount")}</PartIILine13aAmount>
    <PartIILine13bTaxTreatyCountry>${getValue("Form-8233-Part_3_scholarship_tax_treaty")}</PartIILine13bTaxTreatyCountry>
    <PartIILine13bTaxTreatyArticle>${getValue("Form-8233-Part_3_scholarship_treaty_article")}</PartIILine13bTaxTreatyArticle>
    <PartIILine13cExemptTotal>${getValue("Form-8233-Part_3_scholarship_total_income_under_treaty")}</PartIILine13cExemptTotal>
    <PartIILine14SufficientFacts>${getValue("Form-8233-Part_2_sufficient_facts")}</PartIILine14SufficientFacts>
    <PartIILine15NumberOfExemptions />
    <PartIILine16NumberOfDays />
    <PartIILine17DailyExemptionAmount />
    <PartIILine18ExemptionAmountClaimed />
    <SubstituteFormStatement>The Internal Revenue Service does not require your consent to any provisions of this document other than the certifications required to establish your status as a non-U.S. person and, if applicable, obtain a reduced rate of withholding.</SubstituteFormStatement>
  </FORM_8233_SEP_2018>`;

  // console.log("Form8233 XML generated successfully : ", xml);
  return escapeXmlText(xml);
}
